import { useEffect, useMemo, useState } from "react";

import type { DAGData } from "../../api/artifacts";
import { DagViewerSurface } from "../dag-viewer";

interface KestrelNode {
  id: string;
  type: string;
  kind?: string;
  t: string;
  label: string;
  state?: string | null;
  anchor?: boolean;
  channel?: string | null;
}

interface KestrelEdge {
  s: string;
  d: string;
  k: string;
}

interface KestrelGraphPayload {
  nodes: KestrelNode[];
  edges: KestrelEdge[];
}

/** The Kestrel fixture now exercises the same renderer shipped to every V5 DAG. */
export function KestrelGraphSurface() {
  const [payload, setPayload] = useState<KestrelGraphPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/demo-v3/kestrel/graph", { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`graph payload ${response.status}`);
        return response.json() as Promise<KestrelGraphPayload>;
      })
      .then(setPayload)
      .catch((reason) => {
        if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : String(reason));
      });
    return () => controller.abort();
  }, []);

  const graph = useMemo<DAGData | null>(() => payload ? ({
    nodes: payload.nodes.map((node) => ({
      ...node,
      id: node.id,
      path: `kestrel.${node.type}.${node.id}`,
      kind: node.kind ?? node.type,
      name: node.label,
      type: node.type,
      created_at: node.t,
    })),
    edges: payload.edges.map((edge, index) => ({
      id: `kestrel-edge-${index}`,
      parent_id: edge.s,
      child_id: edge.d,
      relationship: edge.k,
    })),
  }) : null, [payload]);

  if (error) {
    return (
      <div className="v5-kestrel-graph" data-testid="kestrel-graph-surface">
        <p className="v5-muted">Could not load the Kestrel graph payload: {error}</p>
      </div>
    );
  }
  if (!graph) {
    return (
      <div className="v5-kestrel-graph" data-testid="kestrel-graph-surface">
        <p className="v5-muted">Loading the Kestrel lineage…</p>
      </div>
    );
  }
  return (
    <div className="v5-kestrel-graph" data-testid="kestrel-graph-surface">
      <DagViewerSurface
        projectId="kestrel-demo"
        projectName="Project Kestrel · the whole graph"
        branch="main"
        dataOverride={graph}
        selectedNodeId={selectedId}
        onSelectNode={setSelectedId}
      />
    </div>
  );
}
